import { memo, useCallback, useMemo, type FC, type ChangeEvent, type MouseEvent, type ReactNode } from 'react';
import classnames from 'clsx';

import style from './checkbox.module.less';
import { noop } from '../../default-props/noop';
import { GuidService } from '../../services/guid-service';

type CheckboxChangeHandler = (checked: boolean, event: ChangeEvent<HTMLInputElement>) => void;

type CheckboxProps = {
  ariaLabel?: string;
  name?: string;
  inputId?: string;
  value?: string;
  disabled?: boolean;
  checked: boolean;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  label?: string;
  largeCheckbox?: boolean;
  indeterminate?: boolean;
  className?: string;
  children?: ReactNode;
  onChange: CheckboxChangeHandler;
  amplitudeTrackingId?: string;
  tabIndex?: number;
  innerClassname?: string;
};

const Checkbox: FC<CheckboxProps> = ({
  ariaLabel = undefined,
  name = '',
  inputId = undefined,
  value = '',
  disabled = false,
  checked,
  onClick = noop,
  label = '',
  largeCheckbox = false,
  indeterminate = false,
  className = '',
  children,
  onChange,
  amplitudeTrackingId = undefined,
  tabIndex = -1,
  innerClassname,
}) => {
  const classesForContainer = classnames(
    style.checkbox,
    className,
    { [style.checked]: checked },
    { [style.disabled]: disabled },
    { [style['large-checkbox']]: largeCheckbox },
    { [style.indeterminate]: indeterminate },
    { [style['no-text']]: !label && !children },
  );
  const inputElementId = useMemo(() => inputId || `checkbox-${GuidService.newGuid()}`, [inputId]);

  const change = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.checked, event);
    },
    [onChange],
  );

  return (
    <div className={classesForContainer} tabIndex={tabIndex}>
      <input
        aria-label={ariaLabel}
        checked={checked}
        data-amplitude-id={amplitudeTrackingId}
        disabled={disabled}
        id={inputElementId}
        name={name || inputElementId}
        onChange={change}
        type='checkbox'
        value={value}
      />
      <label htmlFor={inputElementId}>
        <span
          className={classnames(style['label-text'], innerClassname)}
          data-testid={`checkbox-clickable-${inputElementId}`}
          onClick={onClick}
        >
          <span>
            {label}
            {children}
          </span>
        </span>
      </label>
    </div>
  );
};

const MemoCheckbox = memo(Checkbox);

export { MemoCheckbox as Checkbox };
